import * as STD from '@emersya/mpa-std';
import * as CM from '@emersya/mpa-components-manager';

export interface IPlannerComponentConfiguration extends CM.Component.IConfiguration {
	readonly groundMaterialTree: string | CM.PropertyOperation.TDescriptor;
	readonly doNotDisplayFurniturePriceIfItsZero: boolean;
	readonly size: TViewerComponent.ISize;
	readonly naturalSize: TViewerComponent.ISize;
	readonly applyDefaultSize: boolean;
};

type TDragCommand = HistoryManagerNS.Command<'viewer/drag', {
	localId: number;
	from: TViewerComponent.TVector3;
	to: TViewerComponent.TVector3;
}[]>;

export class PlannerComponent extends CM.Component<
	PlannerComponent,
	IPlannerComponentConfiguration
> {
	static count = 0;
	static self: PlannerComponent;

	static override initialize() {
		CM.Component.initialize.call(this);
		document.addEventListener('mousemove', this.onMouseMove.bind(this));
		document.addEventListener('mouseup', this.onMouseUp.bind(this));
		// this.eventsTypes.push(...Emersya.Viewer.Planner.eventsTypes.filter(x => [
		// 	'message',
		// 	'selection',
		// 	'productAdded',
		// 	'productRemoved'
		// ].includes(x.type)));
		this.declareEventType('planner-loaded');
	};

	override async component_start(startEvent: STD.events.Event<any, CM.Component<any, any>>) {
		await super.component_start(startEvent);

		app.historyManager.registerCommand<TDragCommand>({
			name: 'viewer/drag',
			do: async context => {
				const nodes = context.map(({localId, to}) => {
					const instance = Array.from(this.component_parent.safe.value.productInstances).
					find(instance => instance.localId === localId);

					if (!instance) return undefined;

					return {
						localId,
						to,
						instance
					};
				}).filter(node => node !== undefined);

				await this.component_parent.setProductNodesInstancesTransformations({
					nodes: nodes.map(({localId, to}) => ({
						localId,
						position: to
					}))
				});

				nodes.forEach(({instance, to}) => {
					instance.position.scene.x = to[0];
					instance.position.scene.y = to[1];
					instance.position.scene.z = to[2];
				});
			},
			undo: async context => {
				const nodes = context.map(({localId, from}) => {
					const instance = Array.from(this.component_parent.safe.value.productInstances).
					find(instance => instance.localId === localId);

					if (!instance) return undefined;

					return {
						localId,
						from,
						instance
					};
				}).filter(node => node !== undefined);

				await this.component_parent.setProductNodesInstancesTransformations({
					nodes: nodes.map(({localId, from}) => ({
						localId,
						position: from
					}))
				});

				nodes.forEach(({instance, from}) => {
					instance.position.scene.x = from[0];
					instance.position.scene.y = from[1];
					instance.position.scene.z = from[2];
				});
			},
		});

		app.historyManager.registerCommand<TViewerComponent.TPositionCommand>({
			name: 'viewer/position',
			do: async context => {
				const instance = Array.from(this.component_parent.safe.value.productInstances).
				find(instance => instance.localId === context.localId);

				if (!instance) return;

				await instance.updatePositionRaw(context.to);

				if (app.viewer.menu && app.viewer.currentSelectedInstance === instance)
					await app.viewer.menu.updateWithCurrentProductPosition();
			},
			undo: async context => {
				const instance = Array.from(this.component_parent.safe.value.productInstances).
				find(instance => instance.localId === context.localId);

				if (!instance) return;

				await instance.updatePositionRaw(context.from);

				if (app.viewer.menu && app.viewer.currentSelectedInstance === instance)
					await app.viewer.menu.updateWithCurrentProductPosition();
			}
		});
	}

	private static async onMouseMove(ev: MouseEvent) {
		const self = this.self;
		if (!self) {
			return;
		}
		if (!self.inDragg) {
			return;
		}
		self.cursorUpdatePositionFromMouseEvent(ev);
		const t = ev.target as HTMLElement | null;
		// if cursor is over the viewer
		if (t && t.dataset && (t.dataset.componentType === window.MPAEngineNS.components.Viewer.Viewer.Component.type)) {
			await self.instantiateProduct();
		}
	};

	private static async onMouseUp() {
		if (!this.self) {
			return;
		}
		if (this.self.inDragg) {
			// User released outside viewer before inserting in 3D so cancel.
			await this.self.dragStop();
		} else if (this.self.isDragging) {
			await this.self.dragTaskQueue.push(
				null,
				this.self.onProductNodeInstanceDraggingStoppedOutside.bind(this.self)
			);
		}
	};

	override component_parent: TViewerComponent;
	msTree: string;
	msSetting: string = '';
	naturalSize: TViewerComponent.ISize;
	size: TViewerComponent.ISize;

	async updateGroundUV() {
		const v = app.viewer;
		const currentMaterials = await v.getCurrentMaterials();
		const materials = currentMaterials.materialSettingsConfiguration;
		if (!materials) {
			this.component_throwLibraryError('No ground materials settings found!', {
				currentMaterials
			});
		}
		const material = materials.find(x => x.materialTree === this.msTree);
		if (!material) {
			this.component_throwLibraryError('No ground material found!', {
				currentMaterials,
				expectedMSTree: this.msTree
			});
		}
		await v.transformTexture({
			configurableMaterial: this.msTree,
			materialVariation: this.msSetting,
			contents: [
				'albedo',
				'albedo.specular',
				'albedo.specularIntensity',
				'roughness',
				'roughness.sheen',
				'roughness.sheenIntensity',
				'roughness.sheenRoughness',
				'metalness',
				'metalness.anisotropy',
				'metalness.anisotropyIntensity',
				'metalness.anisotropyRoughness',
				'metalness.anisotropyRotation',
				'opacity',
				'normalDeformation',
				'normalDeformationIntensity',
				'normalDeformation2',
				'normalDeformationIntensity2',
				'translucency',
				'emissive',
				'emissiveIntensity',
				'emissive.glowColor',
				'emissive.glowOpacity',
				'emissive.glowSaturation',
				'iridescence',
				'coatingIntensity',
				'coatingThickness',
				'coatingRoughness',
				'occlusionAmbient',
				'occlusionLight'
			],
			transform: {
				rotation: 0,
				translation: [0, 0, 0],
				rotationCenter: [0, 0, 0],
				scaling: [
					this.size.width,
					this.size.depth,
					this.size.height
				]
			}
		});
	};

	private async setSize(size: Partial<TViewerComponent.ISize>) {
		if (size.depth) {
			this.size.depth = size.depth;
		}
		if (size.width) {
			this.size.width = size.width;
		}
		if (size.height) {
			this.size.height = size.height;
		}
		await app.viewer.resizePlannerSpace({
			length: this.size.width,
			width: this.size.depth,
			height: this.size.height
		});
	};

	async updateSize(
		what: 'width' | 'depth' | 'height',
		value: number
	): Promise<void>;
	async updateSize(
		size: Partial<TViewerComponent.ISize>
	): Promise<void>;
	async updateSize(
		...args: [
				'width' | 'depth' | 'height',
			number
		] | [
			Partial<TViewerComponent.ISize>
		]
	): Promise<void> {
		const v = app.viewer;
		await v.freezeRendering({keepCount: true});
		if (args.length === 1) {
			await this.setSize(args[0]);
		} else {
			await this.setSize({[args[0]]: args[1]});
		}
		await this.updateGroundUV();
		await v.unfreezeRendering({keepCount: true});
	};

	async updateMs(msSetting: string) {
		this.msSetting = msSetting;
		const v = app.viewer;
		await v.freezeRendering({keepCount: true});
		await this.updateGroundUV();
		await v.setMaterials({
			nodeLocalIds: [1],
			materials: [
				{
					configurableMaterial: this.msTree,
					materialVariation: this.msSetting
				}
			]
		});
		await v.unfreezeRendering({keepCount: true});
	};

	async onPlannerLoaded() {
		// const extendedProducts = (
		// 	this.planner.categories.map(x => x.products.filter(y => y.extension)).filter(x => x.length).flat()
		// );
		// for (const extendedProduct of extendedProducts) {
		// 	const extension = <NonNullable<typeof extendedProduct.extension>> extendedProduct.extension;
		// 	const id = 'plannerProduct.' + extendedProduct.getId();
		// 	if (extension.name) {
		// 		API.translationsManager.addObject(id + '.name', extension.name);
		// 	}
		// 	if (extension.description) {
		// 		API.translationsManager.addObject(id + '.description', extension.description);
		// 	}
		// }
		await this.dispatchEvent('planner-loaded', {});
	};

	async directInstanciation(product: TViewerComponent.Product) {
		await product.instantiate(1, 'drop', [], false, null);
		// await product.instantiate(1, false, null);
	};

	async dragStart( //eslint-disable-line require-await
		ev: MouseEvent,
		product: TViewerComponent.Product
	) {
		if (this.inDragg) {
			this.component_throwLibraryError('Drag start event handler triggered but already in drag!', {});
		}
		if (!ev.target || !(ev.target instanceof HTMLElement)) {
			this.component_throwLibraryError('', {});
		}
		document.body.classList.add('unselectable');
		app.viewer.disableIframeInteractions();
		this.inDragg = {
			product,
			source: ev.target,
			iconDOM: product.iconDOM.cloneNode(true) as HTMLImageElement
		};
		this.inDragg.iconDOM.classList.add('plannerInDraggComponentIcon');
		this.component_parent.application.component_DOM.classList.add('planner-inDragg');
		this.cursorUpdatePositionFromMouseEvent(ev);
		document.body.appendChild(this.inDragg.iconDOM);
	};

	async dragStop() { //eslint-disable-line
		if (!this.inDragg) {
			this.component_throwLibraryError('Drag stop event handler triggered but not in drag!', {});
		}
		document.body.classList.remove('unselectable');
		this.component_parent.enableIframeInteractions();
		this.component_parent.application.component_DOM.classList.remove('planner-inDragg');
		this.inDragg.iconDOM.remove();
		this.inDragg = null;
	};

	private dragTaskQueue: TViewerComponent.TaskQueue;

	private inDragg: {
		source: HTMLElement;
		product: TViewerComponent.Product;
		iconDOM: HTMLImageElement;
	} | null = null;
	private instantiationPromise: Promise<TViewerComponent.ProductInstance> | null = null;

	isDragging: boolean = false;

	private draggedInstances: Map<
		TViewerComponent.ProductInstance,
		{
			startPosition: TViewerComponent.TVector3;
			endPosition?: TViewerComponent.TVector3;
		}
	> = new Map();

	private needDraggingGroupUpdate: boolean = false;

	private async onProductNodeInstanceDraggingStarted(ev: STD.events.Event<TViewerComponent, any>) {
		this.isDragging = true;
		const instance = Array.from(this.component_parent.safe.value.productInstances).
		find(instance => instance.localId === ev.details.nodeLocalId)!;

		this.needDraggingGroupUpdate = (
			this.component_parent.safe.currentSelectedInstances.size > 0 &&
			!this.component_parent.safe.currentSelectedInstances.has(instance)
		);

		if (this.needDraggingGroupUpdate)
			await this.component_parent.unselectAll(false);

		const {x, y, z} = instance.position.scene;
		this.draggedInstances.set(instance, {startPosition: [x, y, z]});
	}

	private async onProductNodeInstanceDraggingStopped(ev: STD.events.Event<TViewerComponent, any>) {
		if (this.instantiationPromise)
			await this.instantiationPromise;

		if (!this.isDragging || this.draggedInstances.size === 0)
			this.component_throwLibraryError('unexpected drag stop', {});

		const draggedInstance = Array.from(this.draggedInstances).
		find(([instance]) => instance.localId === ev.details.nodeLocalId)!;

		if (draggedInstance === undefined)
			this.component_throwLibraryError('no dragged instance with this local id', {localId: ev.details.nodeLocalId});

		const instance = draggedInstance[0];

		if (this.needDraggingGroupUpdate)
			await this.component_parent.safe.updateDraggingGroup();

		if (instance.isInInitialDrag) {
			instance.isInInitialDrag = false;
			this.draggedInstances.clear();
			this.isDragging = false;
			return;
		}

		draggedInstance[1].endPosition = [
			ev.details.position[0],
			ev.details.position[1],
			ev.details.position[2]
		];

		if (Array.from(this.draggedInstances).every(([_, {endPosition}]) => endPosition !== undefined)) {
			const commandContext = Array.from(this.draggedInstances).map(([instance, {startPosition, endPosition}]) => ({
				localId: instance.localId,
				from: startPosition,
				to: endPosition!
			}));

			app.historyManager.addExecuted<TDragCommand>(
				'viewer/drag',
				commandContext
			);

			this.draggedInstances.clear();
			this.isDragging = false;
		}
	};

	private async onProductNodeInstanceDraggingStoppedOutside() {
		for (const [instance] of Array.from(this.draggedInstances)) {
			if (!instance.isInInitialDrag || instance.isDeleted) continue;

			await instance.delete();
			this.draggedInstances.delete(instance);
		}

		if (this.draggedInstances.size === 0)
			this.isDragging = false;
	}

	/**
	 * this.inDragg should be manually checked before call
	 **/
	private cursorUpdatePositionFromMouseEvent(ev: MouseEvent) {
		const inDrag = this.inDragg as NonNullable<typeof this.inDragg>;
		const i = inDrag.iconDOM;
		i.style.left = (ev.x - (i.naturalWidth / 2)) + 'px';
		i.style.top = (ev.y - (i.naturalHeight / 2)) + 'px';
	};

	/**
	 * this.inDragg should be manually checked before call
	 **/
	private async instantiateProduct() {
		const inDrag = this.inDragg as NonNullable<typeof this.inDragg>;
		const product = inDrag.product;
		await this.dragStop();
		this.instantiationPromise = product.instantiate(1, 'drop', [], false, {isUserInserted: true});
		const instance = await this.instantiationPromise;
		instance.isInInitialDrag = true;
	};

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component>[0],
		configuration: IPlannerComponentConfiguration
	) {
		super(descriptor, configuration);
		if (PlannerComponent.count !== 0) {
			this.component_throwUserError('Only one planner component is allowed!', {
				PlannerComponent
			});
		}
		PlannerComponent.self = this;
		this.component_parent = <TViewerComponent>descriptor.parent;
		if (!(this.component_parent instanceof window.MPAEngineNS.components.Viewer.Viewer.Component)) {
			this.component_throwUserError(
				'Planner component should be a direct child of a viewer component!',
				{parent: this.component_parent}
			);
		}
		const appComponent = this.component_getRoot();
		if (!(appComponent instanceof window.MPAEngineNS.components.Application.Component)) {
			this.component_throwUserError(
				'Root component must be an instance of an application component!',
				{root: appComponent}
			);
		}
		if (!configuration.size) {
			this.component_throwUserError(
				'Planner configuration should includes size object!',
				{configuration}
			);
		}
		this.size = {...configuration.size};
		this.naturalSize = {...configuration.naturalSize};
		this.msTree = (
			(typeof configuration.groundMaterialTree === 'string') ? configuration.groundMaterialTree :
				''
		);
		PlannerComponent.count++;
		this.dragTaskQueue = new TViewerComponent.TaskQueue();
		this.component_parent.addEventListener(
			'message',
			event => this.dragTaskQueue.push(event, this.onProductNodeInstanceDraggingStarted.bind(this)),
			{
				listener: this,
				subType: 'onProductNodeInstanceDraggingStarted'
			}
		);
		this.component_parent.addEventListener(
			'message',
			event => this.dragTaskQueue.push(event, this.onProductNodeInstanceDraggingStopped.bind(this)),
			{
				listener: this,
				subType: 'onProductNodeInstanceDraggingStopped'
			}
		);
	};
};
export namespace NS {
	export const Component = PlannerComponent;
	export type Component = PlannerComponent;
	export type Configuration = IPlannerComponentConfiguration;
}
import TViewerComponent from '../../Viewer/Viewer';
import {NS as HistoryManagerNS} from '../../../HistoryManager';

CM.Manager.declare({
	id: 'viewer-planner',
	type: PlannerComponent
});
